import React from 'react';
import ReactDOM from 'react-dom';
import { Plugins, StatusBarStyle } from '@capacitor/core';
import { Logger, DEFAULT_LOG_LEVEL } from '@mummssoftware/logger';
// eslint-disable-next-line import/extensions, import/no-unresolved
import PatientPortal from './portal';
import { DEFAULT_LOCALE } from './runtime-constants';
import kcConfig from './security';
import { setTokens, nativeInitSettings } from './native';

let keycloak;

const { StatusBar } = Plugins;

const loggingServiceHost = window.env.hosts?.loggingServiceHost;
const loggingServicePath = window.env.hosts?.loggingServicePath || '/log/';
const loggingServiceInterval =
  window.env.hosts?.loggingServiceInterval || 60 * 1000; // default 60 seconds
let hostString = loggingServiceHost;
if (!!hostString && hostString.endsWith('/'))
  hostString = hostString.substring(0, hostString.length - 1);

let pathString = loggingServicePath || '';
if (pathString.startsWith('/')) pathString = pathString.substring(1);

const loggingServiceUrl = `${hostString}/${pathString}`;

const logger = Logger({
  url: loggingServiceUrl,
  prefix: 'CertApp',
  metricNamespacePrefix: 'CertApp',
  logLevel: DEFAULT_LOG_LEVEL,
  flushInterval: loggingServiceInterval,
});
logger.addListeners();

const logout = async () => {
  // clear tokens: Error: Redirection to URL with a scheme that is not HTTP(S)
  localStorage.setItem('keycloak-tokens', JSON.stringify({}));
  console.log('LOGOUT localStorage', localStorage);

  await StatusBar.setStyle({
    style: StatusBarStyle.Light,
  }).catch(() => null);
  const keycloakLogoutUrl = window.env.hosts?.keycloaklogouturl;
  keycloak.logout(
    keycloakLogoutUrl ? { redirectUri: `${keycloakLogoutUrl}` } : {},
  );
};

const timeout = async () => {
  await StatusBar.setStyle({ style: StatusBarStyle.Light }).catch(() => null);
  const keycloakTimeoutUrl = window.env.hosts?.keycloaktimeouturl;
  keycloak.logout(
    keycloakTimeoutUrl ? { redirectUri: `${keycloakTimeoutUrl}` } : {},
  );
};

StatusBar.setStyle({ style: StatusBarStyle.Light }).catch(() => null);

/*
  Wait - This is a promise that will introduce a delay in ms to a promise chain
    This is a candidate for refactoring into a utility function. 
 */
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/*
  retryOperation - This is a promise that performs a recursive retry using an incremental 
    backoff. It requires the `wait` function defined above.
    This is a candidate for refactoring into a utility function. 
  
  Usage:
    Define an operation to be retried as a promise....

    const someOperation = () => 
      new Promise(resolve, reject) => {
        try {
          resultValue = somefunction();
          resolve(resultValue);
        }
        catch (error) {
          reject(error);
        }
      }
    }
    ...

    Call the retry function for that operation. Note that numTries and maxTries are 
    optional. By default, we will try a max of 5 times with an incremental backoff 
    starting at 500 ms (1/2 second),

    const numTries = 0;
    const maxTries = 5;
    retryOperation(someOperation, numTries, maxTries)
      .then((response) => {
        console.log(`someOperation returned ${response}`)
        doOtherThingsThatDependOnSuccess();
      })
      .catch((error) => {
        console.log(
          `someOperation failed with error ${error}`,
        );
        doSomethingOnOperationFailure();
      });
 */
const retryOperation = (operation, numAttempts = 0, maxAttempts = 5) => {
  const delay = 500;
  return new Promise((resolve, reject) => {
    console.log(`retryOperation: Trying operation...`);
    operation()
      .then(resolve)
      .catch((reason) => {
        console.log(`retryOperation: operation failed with reason ${reason}`);
        if (numAttempts < maxAttempts) {
          console.log(`retryOperation: Retrying ${numAttempts}/${maxAttempts}`);
          return wait(delay * (numAttempts + 1))
            .then(retryOperation.bind(null, operation, numAttempts + 1))
            .then(resolve)
            .catch(reject);
        }
        return reject(reason);
      });
  });
};

const initializeSessionManagement = () =>
  new Promise((resolve, reject) => {
    const pimBaseurl = window.env?.hosts?.pim;
    const pimTTLEndpoint = window.env?.hosts?.pimttlendpoint;
    const sessionUrlBase = window.env?.hosts?.sessionhost;
    const sessionConfig = {
      keycloak,
      sessionUnifierUrl: `${sessionUrlBase}/session/`,
      pimTTLUrl: `${pimBaseurl}${pimTTLEndpoint}`,
      logoutFunction: timeout,
    };
    try {
      window.sessionManagement.initializeSessionManagement(sessionConfig);
      return resolve('Initalization successful!');
    } catch (error) {
      return reject(error);
    }
  });

const initializeKeycloak = () =>
  new Promise((resolve, reject) => {
    try {
      const kc = window.Keycloak(kcConfig);
      if (kc) return resolve(kc);
      return reject(
        new Error(
          'getKc: An unknown error occurred trying to initialize keycloak',
        ),
      );
    } catch (error) {
      return reject(error);
    }
  });

const loadApplication = () => {
  keycloak.onAuthLogout = function () {
    logout();
  };
  keycloak
    .init({
      onLoad: 'login-required',
      // TODO: Alt native mobile check...?
      ...(window.cordova ? nativeInitSettings() : {}),
    })
    .success(async (authenticated) => {
      if (authenticated) {
        // Will only succeed on native
        await StatusBar.setStyle({ style: StatusBarStyle.Dark })
          .then(() => setTokens(keycloak))
          .catch(() => null);
        // Returns promise, not implemented in web view so we can ignore
        const queryParams = new URLSearchParams(window.location.search);
        const theme = queryParams.get('theme');
        const locale = queryParams.get('locale') || DEFAULT_LOCALE;
        const agency = queryParams.get('agency');

        document.addEventListener('deviceready', function onDeviceReady() {
          window.addEventListener(
            'keyboardDidShow',
            function onKeyboardDiShow() {
              document.activeElement.scrollIntoView();
            },
          );
        });

        retryOperation(initializeSessionManagement)
          .then((response) =>
            console.log(`Session Management initialized: ${response}`),
          )
          .catch((error) => {
            console.log(
              `Session Management was unable to be initialized: ${error}`,
            );
          });

        ReactDOM.render(
          <PatientPortal
            key={locale}
            keycloak={keycloak}
            locale={locale}
            logout={logout}
            theme={theme}
            agency={agency}
            logger={logger}
          />,
          document.querySelector('#root'),
        );
      } else {
        // this shouldn't occur, but if it does...
        logout();
      }
    });
};

console.log(
  `DEBUG: setting up keycloak with config: ${JSON.stringify(kcConfig)}`,
);
retryOperation(initializeKeycloak)
  .then((kc) => {
    console.log('Keycloak was successfully initialized');
    keycloak = kc;
    loadApplication();
  })
  .catch((error) => {
    console.log(`Keycloak was unable to be initialized: ${error}`);
    window.alert(
      'An error occurred initializing the application. Please try again in a few minutes. If the issue persists, please contact support.',
    );
    throw new Error('ERROR: keycloak could not be initialized');
  });
